import { FC, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import { CompaniesService } from 'api';
import {
  COMPANY_FINANCIAL_HISTORY_QUERY_KEY,
  COMPANY_FUNDING_ROUNDS_QUERY_KEY,
  COMPANY_NEWS_QUERY_KEY,
} from 'common/constants/services/companies';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import CommonProfileConfirmationBanner from 'components/CommonProfileConfirmationBanner';
import EditCommonProfileDialog from 'components/EditCommonProfileDialog';
import { useGetCompanyCapIQOverview, useGetCompanyMutualFundMarks } from 'services/hooks/companies';
import { useUserUtils } from 'utilities/userUtils';
import { CompanyOverviewProps } from './CompanyOverview.props';
import {
  CardBody,
  CardTitle,
  CardTitleRow,
  CompanyInfoGrid,
  CompanyInfoSection,
  ExecutiveList,
  OutlinedCard,
  SectionHeader,
  SoftChip,
  SourceChip,
  TagRow,
  WebsiteLink,
} from './CompanyOverview.styles';
import { FinancialHistoryCard } from './FinancialHistoryCard';
import { FundingRoundsCard } from './FundingRoundsCard';
import { MutualFundMarksCard } from './MutualFundMarksCard';
import { EndDateMode, EndDateModeValue } from './MutualFundMarksCard/MutualFundMarksCard.utils';
import { NewsAndPressReleasesCard } from './NewsAndPressReleasesCard';
import { ZXIndexValueCard } from './ZXIndexValueCard';

const CompanyOverview: FC<CompanyOverviewProps> = ({ measurementDate }) => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [endDateMode, setEndDateMode] = useState<EndDateModeValue>(EndDateMode.Measurement);
  const { isStaffUser, isSuperUser } = useUserUtils();

  const commonProfileId = companyInfo?.common_profile_id as string | null | undefined;
  const commonProfileConfirmed = companyInfo?.common_profile_confirmed as boolean | null | undefined;
  const isProfileUnconfirmed = commonProfileId != null && commonProfileConfirmed === false;
  const isProfileConfirmed = commonProfileConfirmed === true;

  const { data, isLoading } = useGetCompanyCapIQOverview(isProfileConfirmed ? companyInfo?.id : undefined);
  const { data: marksData, isLoading: marksLoading } = useGetCompanyMutualFundMarks(
    isProfileConfirmed ? companyInfo?.id : undefined
  );

  const handleSacraRefresh = async () => {
    if (!companyInfo?.id || refreshing) {
      return;
    }
    setRefreshing(true);
    try {
      await CompaniesService.apiCompaniesSacraCacheRefreshCreate(companyInfo.id);
      await queryClient.invalidateQueries({ queryKey: [COMPANY_FINANCIAL_HISTORY_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [COMPANY_FUNDING_ROUNDS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [COMPANY_NEWS_QUERY_KEY] });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Grid container padding={2} spacing={2}>
      {isProfileUnconfirmed && (
        <Grid size={{ xs: 12 }}>
          <CommonProfileConfirmationBanner
            isStaff={isStaffUser() || isSuperUser()}
            onEditProfile={() => setEditProfileOpen(true)}
          />
        </Grid>
      )}

      {measurementDate && (
        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Data as of
          </Typography>
          <ToggleButtonGroup
            value={endDateMode}
            exclusive
            size="small"
            onChange={(_e, v: EndDateModeValue | null) => {
              if (v) {
                setEndDateMode(v);
              }
            }}>
            <ToggleButton value={EndDateMode.Measurement}>{measurementDate}</ToggleButton>
            <ToggleButton value={EndDateMode.Latest}>Latest</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      {editProfileOpen && commonProfileId != null && (
        <EditCommonProfileDialog
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          profileId={Number(commonProfileId)}
          companyId={companyInfo?.id}
        />
      )}

      {/* Company Information */}
      <Grid size={{ xs: 12 }}>
        <OutlinedCard variant="outlined">
          <CardBody>
            <CardTitleRow>
              <CardTitle>Company Information</CardTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {data && <SourceChip label="Capital IQ" size="small" chipColor="blue" />}
                {isProfileConfirmed && (
                  <Tooltip title="Refresh Sacra data">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleSacraRefresh}
                        disabled={refreshing}
                        sx={{ color: 'text.secondary' }}>
                        <RefreshIcon
                          fontSize="small"
                          sx={{
                            animation: refreshing ? 'spin 1s linear infinite' : 'none',
                            '@keyframes spin': {
                              from: { transform: 'rotate(0deg)' },
                              to: { transform: 'rotate(360deg)' },
                            },
                          }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>
            </CardTitleRow>

            {isLoading && (
              <Grid container spacing={3}>
                {[0, 1, 2].map(i => (
                  <Grid key={i} size={{ xs: 12, md: 4 }}>
                    <Skeleton variant="text" width="60%" height={14} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="100%" height={14} />
                    <Skeleton variant="text" width="90%" height={14} />
                    <Skeleton variant="text" width="80%" height={14} />
                  </Grid>
                ))}
              </Grid>
            )}

            {!isLoading && (
              <CompanyInfoGrid>
                {/* Left: website / status / year / HQ */}
                <CompanyInfoSection>
                  {companyInfo?.website && (
                    <>
                      <SectionHeader>Website</SectionHeader>
                      <WebsiteLink href={companyInfo.website} target="_blank" rel="noopener noreferrer">
                        {companyInfo.website.replace(/^https?:\/\//, '')}
                      </WebsiteLink>
                    </>
                  )}
                  <SectionHeader sx={{ mt: companyInfo?.website ? 1.5 : 0 }}>Status</SectionHeader>
                  <div>
                    <SoftChip label="Operating" size="small" chipColor="green" />
                  </div>
                  {companyInfo?.vintage_year && (
                    <>
                      <SectionHeader sx={{ mt: 1.5 }}>Year Founded</SectionHeader>
                      <Typography variant="body2">{companyInfo.vintage_year}</Typography>
                    </>
                  )}
                  {data?.headquarters && (
                    <>
                      <SectionHeader sx={{ mt: 1.5 }}>Headquarters</SectionHeader>
                      <Typography variant="body2">{data.headquarters}</Typography>
                    </>
                  )}
                  {data?.cap_iq_company_id && (
                    <>
                      <SectionHeader sx={{ mt: 1.5 }}>Cap IQ ID</SectionHeader>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {data.cap_iq_company_id}
                      </Typography>
                    </>
                  )}
                </CompanyInfoSection>

                {/* Center: business description + industry tags */}
                <CompanyInfoSection>
                  {data?.business_description ? (
                    <>
                      <SectionHeader>Business Description</SectionHeader>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 0.5 }}>
                        {data.business_description}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No business description available.
                    </Typography>
                  )}
                  {data?.primary_industry && (
                    <TagRow sx={{ mt: 1.5 }}>
                      <SoftChip label={data.primary_industry} size="small" chipColor="blue" />
                      {data.sic_code && <SoftChip label={`SIC ${data.sic_code}`} size="small" chipColor="blue" />}
                    </TagRow>
                  )}
                </CompanyInfoSection>

                {/* Right: executives + investors */}
                <CompanyInfoSection>
                  {data?.key_executives?.length ? (
                    <>
                      <SectionHeader>Key Executives</SectionHeader>
                      <ExecutiveList>
                        {data.key_executives.map((exec, i) => (
                          <Typography key={i} variant="body2">
                            <strong>{exec.name}</strong>
                            {exec.title && (
                              <Typography component="span" variant="body2" color="text.secondary">
                                {'  '}
                                {exec.title}
                              </Typography>
                            )}
                          </Typography>
                        ))}
                      </ExecutiveList>
                    </>
                  ) : null}
                  {data?.key_investors?.length ? (
                    <>
                      <SectionHeader sx={{ mt: data?.key_executives?.length ? 2 : 0 }}>Key Investors</SectionHeader>
                      <TagRow>
                        {data.key_investors.map((investor, i) => (
                          <SoftChip key={i} label={investor} size="small" chipColor="grey" />
                        ))}
                      </TagRow>
                    </>
                  ) : null}
                  {!data && !isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No Capital IQ data available.
                    </Typography>
                  )}
                </CompanyInfoSection>
              </CompanyInfoGrid>
            )}
          </CardBody>
        </OutlinedCard>
      </Grid>

      {/* ZX Index Value (Zanbato) */}
      {isProfileConfirmed && (
        <Grid size={{ xs: 12 }}>
          <ZXIndexValueCard companyId={companyInfo?.id} measurementDate={measurementDate} endDateMode={endDateMode} />
        </Grid>
      )}

      {/* Mutual Fund Marks */}
      <Grid size={{ xs: 12 }}>
        <MutualFundMarksCard
          companyId={companyInfo?.id}
          data={marksData}
          isLoading={marksLoading}
          measurementDate={measurementDate}
          endDateMode={endDateMode}
        />
      </Grid>

      {/* Fundraising Rounds */}
      {isProfileConfirmed && (
        <Grid size={{ xs: 12 }}>
          <FundingRoundsCard companyId={companyInfo?.id} />
        </Grid>
      )}

      {/* Financial History + News side by side */}
      {isProfileConfirmed && (
        <Grid size={{ xs: 12, md: 7 }}>
          <FinancialHistoryCard companyId={companyInfo?.id} />
        </Grid>
      )}

      {isProfileConfirmed && (
        <Grid size={{ xs: 12, md: 5 }}>
          <NewsAndPressReleasesCard companyId={companyInfo?.id} />
        </Grid>
      )}
    </Grid>
  );
};

export { CompanyOverview };
